/**
 * Integrations API client — connected external accounts (calendars in v1).
 *
 * Field names are snake_case: they mirror the backend JSON struct tags exactly
 * (repo convention — CLAUDE.md "API & schema discipline").
 *
 * NOTE: there is deliberately no credential field anywhere in these types. The
 * backend's Integration struct has none (the ciphertext is not serialized and
 * consecutive_failures is `json:"-"`), so the only account identifier that ever
 * crosses the wire is the masked `account_label`.
 */
import { apiClient } from './index';

export type IntegrationProvider = 'google_calendar' | 'icloud_caldav';
export type IntegrationStatus = 'active' | 'error' | 'revoked';

export interface Integration {
  id: string;
  tenant_id: string;
  user_id: string;
  provider: IntegrationProvider;
  account_label: string; // masked; never the raw address
  status: IntegrationStatus;
  last_sync_at: string | null; // RFC3339; null until the first successful sync
  last_error: string | null; // null when healthy
  created_at: string;
  updated_at: string;
}

interface ConsentURLResponse {
  consent_url: string;
}

export async function listIntegrations(): Promise<Integration[]> {
  // The backend returns a BARE JSON array (no {data:[...]} envelope) — the same
  // contract as /assistant/channels. `?? []` guards a 200 with a JSON `null`
  // body (Go marshals a nil slice as null).
  const { data } = await apiClient.get<Integration[]>('/integrations');
  return data ?? [];
}

export async function getGoogleConsentURL(): Promise<string> {
  const { data } = await apiClient.get<ConsentURLResponse>(
    '/integrations/google/connect',
  );
  return data.consent_url;
}

/**
 * Connect an iCloud calendar.
 *
 * `appPassword` MUST be an Apple app-specific password from appleid.apple.com,
 * not the user's Apple ID password (iCloud rejects that on any 2FA account).
 * The backend validates it by actually connecting before it stores anything, so
 * a bad password is an immediate 400 rather than a silent failure in a
 * background job half an hour later.
 */
export async function connectICloud(
  appleId: string,
  appPassword: string,
): Promise<Integration> {
  const { data } = await apiClient.post<Integration>('/integrations/icloud', {
    apple_id: appleId,
    app_password: appPassword,
  });
  return data;
}

export async function deleteIntegration(id: string): Promise<void> {
  await apiClient.delete(`/integrations/${id}`);
}
